'use client';
import React, { useEffect, useState, useMemo } from 'react';
import { DirectoryProvider } from '@/components/directory/DirectoryContext';
import { useDynamicAirtableRecords } from '@/lib/airtable/client-hooks';
import { parseAttachments } from '@/lib/airtable/transform';
import Link from 'next/link';
import { FolderOpen } from 'lucide-react';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import MultiSelect from '@/components/ui/multi-select';
import { Pill } from '@/components/ui/Pill';
import { LoadingPattern } from "@/components/ui/pattern-viewer";
import CRODirectorySection from './CRODirectorySection';
import CROFilterBar from './CROFilterBar';
import CROActiveFilters from './CROActiveFilters';
import { Breadcrumb } from '@/components/ui/breadcrumb';

// NOTE: CRO services are now sourced from the 'CRO_SERVICES' table (logical key: CRO_SERVICES), not the old 'Services' table.

function getDisplayValue(obj: any): string {
  if (!obj || typeof obj !== 'object') return '';
  if (obj.Name) return obj.Name;
  if (obj.name) return obj.name;
  const firstString = Object.values(obj).find(v => typeof v === 'string');
  if (firstString) return String(firstString);
  if (obj.id) return obj.id;
  return '[object]';
}

export default function CROsPage() {
  // Fetch all CROs
  const { data, isLoading, isError } = useDynamicAirtableRecords('CROS');
  // Fetch schema for dynamic field detection
  const [schema, setSchema] = useState<any>(null);
  const [schemaLoading, setSchemaLoading] = useState(true);

  useEffect(() => {
    setSchemaLoading(true);
    fetch('/api/airtable/schema')
      .then(res => res.json())
      .then(data => setSchema(data))
      .catch(() => setSchema(null))
      .finally(() => setSchemaLoading(false));
  }, []);

  // Find the schema for the current table
  const currentTableSchema = useMemo(
    () => schema?.tables?.find((t: any) => t.name === 'CROs' || t.id === 'CROS'),
    [schema]
  );

  // Helper to extract fields for a CRO record
  function extractFields(cro: any) {
    if (!cro || !currentTableSchema) return {};
    // Use schema's primaryFieldId for the name
    const primaryFieldId = currentTableSchema.primaryFieldId;
    const primaryFieldName = currentTableSchema.fields.find((f: any) => f.id === primaryFieldId)?.name || 'Provider';
    // Find description field
    const descriptionField = currentTableSchema.fields.find((f: any) => f.name.toLowerCase().includes('description') || f.name.toLowerCase().includes('summary'))?.name;
    // Find website field
    const fieldsToShow = Object.keys(cro);
    const websiteField = fieldsToShow.find(f => f.toLowerCase().includes('website'));
    // Find location field
    const locationField = fieldsToShow.find(f => f.toLowerCase() === 'location');
    // Slug field
    const slugField = fieldsToShow.find(f => f.toLowerCase() === 'slug');

    // Extract values
    const attachments = (cro['Logo'] && Array.isArray(cro['Logo']))
      ? parseAttachments(cro['Logo']) ?? []
      : [];
    const logo = attachments.length > 0 ? attachments[0] : null;
    const logoSrc = logo && typeof logo === 'object' ? (logo.thumbnailLarge ?? logo.url ?? '') : '';
    const name = cro[primaryFieldName] || '';
    const description = descriptionField ? cro[descriptionField] : '';
    const website = websiteField ? cro[websiteField] : undefined;
    let websiteUrl = website;
    if (website && typeof website === 'string' && !/^https?:\/\//i.test(website)) websiteUrl = 'https://' + website;
    let location = locationField ? cro[locationField] : undefined;
    if (!location) {
      const city = cro['City'] || '';
      const state = cro['State'] || '';
      const country = cro['Country'] || '';
      location = [city, state, country].filter(Boolean).join(', ');
    }
    const slug = slugField ? cro[slugField] : undefined;
    return { logoSrc, name, description, website, websiteUrl, location, slug };
  }

  // Sort CROs alphabetically by name
  const sortedData = Array.isArray(data) ? [...data].sort((a, b) => {
    const nameA = (extractFields(a).name || '').toLowerCase();
    const nameB = (extractFields(b).name || '').toLowerCase();
    return nameA.localeCompare(nameB);
  }) : [];



  // Add state for country filter
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  // Add state for service type filter
  const [selectedServiceTypes, setSelectedServiceTypes] = useState<string[]>([]);
  // Add state for therapeutic area filter
  const [selectedTherapeuticAreas, setSelectedTherapeuticAreas] = useState<string[]>([]);
  // Add state for technology filter
  const [selectedTechnologies, setSelectedTechnologies] = useState<string[]>([]);
  // Add state for platform filter
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  // Add state for instrument filter
  const [selectedInstruments, setSelectedInstruments] = useState<string[]>([]);
  // Add state for software filter
  const [selectedSoftware, setSelectedSoftware] = useState<string[]>([]);
  // Add state for compliance filter
  const [selectedCompliances, setSelectedCompliances] = useState<string[]>([]);

  // Fetch all Technology records for the filter dropdown
  const { data: technologyRecords, isLoading: techLoading } = useDynamicAirtableRecords('TECHNOLOGY');
  const technologyOptions = useMemo(() => {
    if (!technologyRecords || !Array.isArray(technologyRecords)) return [];
    return technologyRecords
      .map((rec: any) => ({ id: rec.id, name: rec['Name'] || rec['Technology Name'] || rec.id }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [technologyRecords]);
  const technologyLookup = useMemo(() => {
    const map: Record<string, string> = {};
    technologyOptions.forEach(opt => { map[opt.id] = opt.name; });
    return map;
  }, [technologyOptions]);
  // Fetch all Platforms records for the filter dropdown
  const { data: platformRecords, isLoading: platformLoading } = useDynamicAirtableRecords('PLATFORMS');
  const platformOptions = useMemo(() => {
    if (!platformRecords || !Array.isArray(platformRecords)) return [];
    return platformRecords
      .map((rec: any) => ({ id: rec.id, name: rec['Name'] || rec['Platform Name'] || rec.id }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [platformRecords]);
  const platformLookup = useMemo(() => {
    const map: Record<string, string> = {};
    platformOptions.forEach(opt => { map[opt.id] = opt.name; });
    return map;
  }, [platformOptions]);

  // Fetch only Instruments products
  const { data: instrumentRecords, isLoading: instrumentsLoading } = useDynamicAirtableRecords(
    'PRODUCTS',
    { filterByFormula: `FIND('Instruments', ARRAYJOIN({Product Type}, ','))` }
  );
  const instrumentOptions = useMemo(() => {
    if (!instrumentRecords || !Array.isArray(instrumentRecords)) return [];
    return instrumentRecords
      .map((rec: any) => ({ id: rec.id, name: rec['Name'] || rec['Product Name'] || rec.id }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [instrumentRecords]);

  // Fetch only Software products
  const { data: softwareRecords, isLoading: softwareLoading } = useDynamicAirtableRecords(
    'PRODUCTS',
    { filterByFormula: `FIND('Software', ARRAYJOIN({Product Type}, ','))` }
  );
  const softwareOptions = useMemo(() => {
    if (!softwareRecords || !Array.isArray(softwareRecords)) return [];
    return softwareRecords
      .map((rec: any) => ({ id: rec.id, name: rec['Name'] || rec['Product Name'] || rec.id }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [softwareRecords]);

  // Extract unique countries from CROs data
  const countryOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const countries = new Set<string>();
    data.forEach((cro: any) => {
      const countryField = cro['Country'];
      if (Array.isArray(countryField)) {
        countryField.forEach((c) => { if (c) countries.add(c); });
      } else if (typeof countryField === 'string' && countryField) {
        countries.add(countryField);
      }
    });
    return Array.from(countries).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Extract unique service types from CROs data
  const serviceTypeOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const types = new Set<string>();
    data.forEach((cro: any) => {
      const field = cro['Service Types'];
      if (Array.isArray(field)) {
        field.forEach((type) => { if (type) types.add(type); });
      } else if (typeof field === 'string' && field) {
        types.add(field);
      }
    });
    return Array.from(types).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Extract unique therapeutic areas from CROs data
  const therapeuticAreaOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const areas = new Set<string>();
    data.forEach((cro: any) => {
      const field = cro['Therapeutic Areas'] || cro['Therapeutic Area'];
      if (Array.isArray(field)) {
        field.forEach((area) => { if (area) areas.add(area); });
      } else if (typeof field === 'string' && field) {
        areas.add(field);
      }
    });
    return Array.from(areas).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Extract unique compliance options from CROs data
  const complianceOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const complianceSet = new Set<string>();
    data.forEach((cro: any) => {
      const field = cro['Regulatory Compliance'];
      if (Array.isArray(field)) {
        field.forEach((val) => { if (val) complianceSet.add(val); });
      } else if (typeof field === 'string' && field) {
        complianceSet.add(field);
      }
    });
    return Array.from(complianceSet).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Add state for view mode (card or list)
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
  
  // Add state for sorting
  const [sortBy, setSortBy] = useState<string>('name-asc');

  // Loading/Error states
  if (isLoading || schemaLoading) {
    return (
      <DirectoryProvider>
        <div className="flex min-h-[60vh] items-center justify-center">
          <LoadingPattern patternName="cell-pattern" size="md" />
        </div>
      </DirectoryProvider>
    );
  }
  if (isError) {
    return (
      <DirectoryProvider>
        <div className="text-center py-20 text-red-500">Error loading CROs.</div>
      </DirectoryProvider>
    );
  }
  if (!data || !Array.isArray(data) || !currentTableSchema) {
    return (
      <DirectoryProvider>
        <div className="text-center py-20">No CROs found.</div>
      </DirectoryProvider>
    );
  }

  return (
    <DirectoryProvider>
      <div className="min-h-screen bg-neutral-50">
        {/* Hero Header */}
        <div className="w-full h-64 md:h-80 bg-gradient-to-r from-primary-500 to-accent-500 shadow-lg overflow-hidden relative rounded-none">
          <div className="max-w-7xl mx-auto w-full h-full flex flex-col md:flex-row md:items-center px-6">
            <div className="w-full">
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                  <span className="text-xl md:text-2xl lg:text-3xl font-medium flex items-center gap-2">
                    <img 
                      src="/LabAtlas-Logo-White-Full.png" 
                      alt="LabAtlas Logo" 
                      className="h-6 w-6 md:h-8 md:w-8 object-contain"
                    />
                    LabAtlas
                  </span>
                Contract Research Organizations
              </h1>
              <p className="text-xl md:text-2xl text-white/90 leading-relaxed">
                Find and compare contract research organizations with LabAtlas' comprehensive directory. Filter by service type, technology, compliance, location, and more to quickly identify the right CRO partner for your spatial biology research needs.
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb 
              items={[
                { label: 'CROs', isCurrentPage: true }
              ]} 
            />
          </div>
          
          {/* Filter Bar (styled per design system) - now lazy loaded and extracted */}
          <CROFilterBar
            countryOptions={countryOptions}
            serviceTypeOptions={serviceTypeOptions}
            therapeuticAreaOptions={therapeuticAreaOptions}
            technologyOptions={technologyOptions}
            platformOptions={platformOptions}
            instrumentOptions={instrumentOptions}
            softwareOptions={softwareOptions}
            complianceOptions={complianceOptions}
            selectedCountries={selectedCountries}
            setSelectedCountries={setSelectedCountries}
            selectedServiceTypes={selectedServiceTypes}
            setSelectedServiceTypes={setSelectedServiceTypes}
            selectedTherapeuticAreas={selectedTherapeuticAreas}
            setSelectedTherapeuticAreas={setSelectedTherapeuticAreas}
            selectedTechnologies={selectedTechnologies}
            setSelectedTechnologies={setSelectedTechnologies}
            selectedPlatforms={selectedPlatforms}
            setSelectedPlatforms={setSelectedPlatforms}
            selectedInstruments={selectedInstruments}
            setSelectedInstruments={setSelectedInstruments}
            selectedSoftware={selectedSoftware}
            setSelectedSoftware={setSelectedSoftware}
            selectedCompliances={selectedCompliances}
            setSelectedCompliances={setSelectedCompliances}
            techLoading={techLoading}
            platformLoading={platformLoading}
            productsLoading={instrumentsLoading || softwareLoading}
          />
          {/* Active Filter Pills - now lazy loaded and extracted */}
          <CROActiveFilters
            selectedCountries={selectedCountries}
            setSelectedCountries={setSelectedCountries}
            selectedServiceTypes={selectedServiceTypes}
            setSelectedServiceTypes={setSelectedServiceTypes}
            selectedTherapeuticAreas={selectedTherapeuticAreas}
            setSelectedTherapeuticAreas={setSelectedTherapeuticAreas}
            selectedTechnologies={selectedTechnologies}
            setSelectedTechnologies={setSelectedTechnologies}
            selectedPlatforms={selectedPlatforms}
            setSelectedPlatforms={setSelectedPlatforms}
            selectedInstruments={selectedInstruments}
            setSelectedInstruments={setSelectedInstruments}
            selectedSoftware={selectedSoftware}
            setSelectedSoftware={setSelectedSoftware}
            selectedCompliances={selectedCompliances}
            setSelectedCompliances={setSelectedCompliances}
            technologyOptions={technologyOptions}
            platformOptions={platformOptions}
            instrumentOptions={instrumentOptions}
            softwareOptions={softwareOptions}
            onClearFilters={() => {
                    setSelectedCountries([]);
                    setSelectedServiceTypes([]);
                    setSelectedTherapeuticAreas([]);
                    setSelectedTechnologies([]);
                    setSelectedPlatforms([]);
                    setSelectedInstruments([]);
                    setSelectedSoftware([]);
                    setSelectedCompliances([]);
                  }}
            viewMode={viewMode}
            setViewMode={setViewMode}
            sortBy={sortBy}
            setSortBy={setSortBy}
          />
          {/* CROs Display - now lazy loaded and extracted */}
          <CRODirectorySection
            filteredCROs={sortedData.filter((cro: any) => {
              const { name } = extractFields(cro);
              // Country filter logic
              if (selectedCountries.length > 0) {
                const countryField = cro['Country'];
                if (Array.isArray(countryField)) {
                  if (!countryField.some((c) => selectedCountries.includes(c))) return false;
                } else if (typeof countryField === 'string') {
                  if (!selectedCountries.includes(countryField)) return false;
                } else {
                  return false;
                }
              }
              // Service Type filter logic
              if (selectedServiceTypes.length > 0) {
                const field = cro['Service Types'];
                if (Array.isArray(field)) {
                  if (!field.some((type) => selectedServiceTypes.includes(type))) return false;
                } else if (typeof field === 'string') {
                  if (!selectedServiceTypes.includes(field)) return false;
                } else {
                  return false;
                }
              }
              // Therapeutic Area filter logic
              if (selectedTherapeuticAreas.length > 0) {
                const field = cro['Therapeutic Areas'] || cro['Therapeutic Area'];
                if (Array.isArray(field)) {
                  if (!field.some((area) => selectedTherapeuticAreas.includes(area))) return false;
                } else if (typeof field === 'string') {
                  if (!selectedTherapeuticAreas.includes(field)) return false;
                } else {
                  return false;
                }
              }
              // Technology filter logic - using new lookup field
              if (selectedTechnologies.length > 0) {
                const techField = cro['Technologies (new)'] || cro['Technologies'];
                if (Array.isArray(techField)) {
                  if (!techField.some((tech) => selectedTechnologies.includes(tech))) return false;
                } else if (typeof techField === 'string') {
                  if (!selectedTechnologies.includes(techField)) return false;
                } else {
                  return false;
                }
              }
              // Platform filter logic - using new lookup field
              if (selectedPlatforms.length > 0) {
                const platformField = cro['Platforms (new)'] || cro['Platforms'];
                if (Array.isArray(platformField)) {
                  if (!platformField.some((platform) => selectedPlatforms.includes(platform))) return false;
                } else if (typeof platformField === 'string') {
                  if (!selectedPlatforms.includes(platformField)) return false;
                } else {
                  return false;
                }
              }
              // Instrument filter logic
              if (selectedInstruments.length > 0) {
                const instrumentsField = cro['Instruments'];
                if (Array.isArray(instrumentsField)) {
                  if (!instrumentsField.some((instrument) => selectedInstruments.includes(instrument))) return false;
                } else if (typeof instrumentsField === 'string') {
                  if (!selectedInstruments.includes(instrumentsField)) return false;
                } else {
                  return false;
                }
              }
              // Software filter logic
              if (selectedSoftware.length > 0) {
                const softwareField = cro['Software'];
                if (Array.isArray(softwareField)) {
                  if (!softwareField.some((software) => selectedSoftware.includes(software))) return false;
                } else if (typeof softwareField === 'string') {
                  if (!selectedSoftware.includes(softwareField)) return false;
                } else {
                  return false;
                }
              }
              // Compliance filter logic
              if (selectedCompliances.length > 0) {
                const complianceField = cro['Regulatory Compliance'];
                if (Array.isArray(complianceField)) {
                  if (!complianceField.some((val) => selectedCompliances.includes(val))) return false;
                } else if (typeof complianceField === 'string') {
                  if (!selectedCompliances.includes(complianceField)) return false;
                } else {
                  return false;
                }
              }
              return !!name && name.trim() !== '';
            }).sort((a: any, b: any) => {
              const { name: nameA, location: locationA } = extractFields(a);
              const { name: nameB, location: locationB } = extractFields(b);
              
              // Get service type for sorting
              const getServiceType = (cro: any) => {
                const serviceField = cro['Service Types'];
                if (Array.isArray(serviceField) && serviceField.length > 0) {
                  return serviceField[0];
                } else if (typeof serviceField === 'string') {
                  return serviceField;
                }
                return '';
              };
              
              const serviceA = getServiceType(a);
              const serviceB = getServiceType(b);
              
              switch (sortBy) {
                case 'name-asc':
                  return nameA.localeCompare(nameB);
                case 'name-desc':
                  return nameB.localeCompare(nameA);
                case 'service-asc':
                  return serviceA.localeCompare(serviceB);
                case 'service-desc':
                  return serviceB.localeCompare(serviceA);
                case 'location-asc':
                  return (locationA || '').localeCompare(locationB || '');
                case 'location-desc':
                  return (locationB || '').localeCompare(locationA || '');
                default:
                  return nameA.localeCompare(nameB);
              }
            })}
            viewMode={viewMode}
            extractFields={extractFields}
            technologyOptions={technologyOptions}
            platformOptions={platformOptions}
            instrumentOptions={instrumentOptions}
            softwareOptions={softwareOptions}
            selectedServiceTypes={selectedServiceTypes}
            selectedTechnologies={selectedTechnologies}
            selectedCompliances={selectedCompliances}
            selectedTherapeuticAreas={selectedTherapeuticAreas}
            selectedPlatforms={selectedPlatforms}
            selectedInstruments={selectedInstruments}
            selectedSoftware={selectedSoftware}
            selectedCountries={selectedCountries}
          />
        </div>
      </div>
      <footer className="w-full py-8 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-4 text-center text-neutral-500 text-sm">
          © 2025 LabAtlas. All rights reserved.
        </div>
      </footer>
    </DirectoryProvider>
  );
} 